import axios from 'axios';
import { API_URL, API_CONFIG, TIMEOUTS } from '../constants/config';
import { defaultFeatureFlagEnabled } from '../constants/featureFlags';
import {
  createTokenRefreshInterceptor,
  getValidAccessToken,
  getAxiosAuthConfig,
} from '../utils/tokenInterceptor';

export interface FeatureFlagCheck {
  flag_name: string;
  is_enabled: boolean;
  reason:
    | 'global'
    | 'role'
    | 'user'
    | 'user_disabled'
    | 'disabled'
    | 'not_found';
}

export interface FeatureFlagsResponse {
  flags: FeatureFlagCheck[];
}

// Create axios instance with configuration
const api = axios.create({
  baseURL: API_URL,
  ...API_CONFIG,
  timeout: TIMEOUTS.API_REQUEST,
  ...getAxiosAuthConfig(),
});

// Add auth token to requests (skip when cookie mode - browser sends cookies)
api.interceptors.request.use(async (config) => {
  const token = await getValidAccessToken();
  if (token && token !== 'cookie') {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Add token refresh interceptor
createTokenRefreshInterceptor(api);

export const featureFlagsAPI = {
  /**
   * Check if a specific feature flag is enabled for the current user
   * Returns a disabled flag if the flag doesn't exist or API call fails
   */
  check: async (flagName: string): Promise<FeatureFlagCheck> => {
    try {
      const response = await api.get<FeatureFlagCheck>('/feature-flags/check', {
        params: { flag_name: flagName },
      });
      return response.data;
    } catch (err: unknown) {
      // If flag doesn't exist (404) or any other error, return disabled flag
      const msg = err instanceof Error ? err.message : String(err);
      console.warn(`Feature flag '${flagName}' check failed:`, msg);
      return {
        flag_name: flagName,
        is_enabled: defaultFeatureFlagEnabled(flagName),
        reason: 'not_found',
      };
    }
  },

  /**
   * Check multiple feature flags for the current user
   * Returns disabled flags for any that don't exist or fail
   */
  checkMultiple: async (flagNames: string[]): Promise<FeatureFlagsResponse> => {
    try {
      const response = await api.get<FeatureFlagsResponse>(
        '/feature-flags/check-multiple',
        {
          params: { flag_names: flagNames.join(',') },
        }
      );
      return response.data;
    } catch (err: unknown) {
      // If API call fails, return all flags as disabled
      const msg = err instanceof Error ? err.message : String(err);
      console.warn('Feature flags check failed:', msg);
      return {
        flags: flagNames.map((name) => ({
          flag_name: name,
          is_enabled: defaultFeatureFlagEnabled(name),
          reason: 'not_found' as const,
        })),
      };
    }
  },
};
